import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [msp, setMsp] = useState(null);  // MSP data
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userType, setUserType] = useState(localStorage.getItem('userType') || 'user'); // 'user' or 'msp_user'
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setTenant(null);
    setMsp(null);
    setUserType('user');
  }, []);

  useEffect(() => {
    const doFetch = async () => {
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          // Check if MSP user
          if (userType === 'msp_user') {
            const response = await axios.get(`${API}/msp/me`);
            setUser(response.data.user);
            setMsp(response.data.msp);
            setTenant(null);
          } else {
            const response = await axios.get(`${API}/auth/me`);
            setUser(response.data.user);
            setTenant(response.data.tenant);
            setMsp(null);
          }
        } catch (error) {
          console.error('Failed to fetch user:', error);
          logout();
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    doFetch();
  }, [token, userType, logout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, tenant: tenantData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('userType', 'user');
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setTenant(tenantData);
    setMsp(null);
    setUserType('user');
    return response.data;
  };

  const signup = async (companyName, adminEmail, adminPassword, adminName) => {
    const response = await axios.post(`${API}/auth/signup`, {
      company_name: companyName,
      admin_email: adminEmail,
      admin_password: adminPassword,
      admin_name: adminName,
    });
    const { access_token, user: userData, tenant: tenantData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('userType', 'user');
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setTenant(tenantData);
    setMsp(null);
    setUserType('user');
    return response.data;
  };

  // MSP Login
  const mspLogin = async (email, password) => {
    const response = await axios.post(`${API}/msp/login`, { email, password });
    const { access_token, user: userData, msp: mspData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('userType', 'msp_user');
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setMsp(mspData);
    setTenant(null);
    setUserType('msp_user');
    return response.data;
  };

  // MSP Signup
  const mspSignup = async (companyName, adminEmail, adminPassword, adminName, website, phone) => {
    const response = await axios.post(`${API}/msp/signup`, {
      company_name: companyName,
      admin_email: adminEmail,
      admin_password: adminPassword,
      admin_name: adminName,
      website: website || null,
      phone: phone || null,
    });
    const { access_token, user: userData, msp: mspData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('userType', 'msp_user');
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setMsp(mspData);
    setTenant(null);
    setUserType('msp_user');
    return response.data;
  };

  // Method to set token directly (for onboarding)
  const setAuthToken = useCallback((newToken) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('userType', 'user');
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUserType('user');
  }, []);

  // Method to set auth from login response (for 2FA flow)
  const setAuthFromResponse = useCallback((data) => {
    const { access_token, user: userData, tenant: tenantData } = data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('userType', 'user');
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setTenant(tenantData);
    setMsp(null);
    setUserType('user');
  }, []);

  // Check if user is MSP
  const isMSP = userType === 'msp_user';
  const isMSPAdmin = isMSP && user?.role === 'msp_admin';

  return (
    <AuthContext.Provider value={{ 
      user, 
      tenant, 
      msp,
      token, 
      loading, 
      userType,
      isMSP,
      isMSPAdmin,
      login, 
      signup, 
      mspLogin,
      mspSignup,
      logout,
      setAuthToken,
      setAuthFromResponse
    }}>
      {children}
    </AuthContext.Provider>
  );
};
